import { useCallback, useEffect, useMemo } from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import type { Person } from "@/hooks/api/use-persons";
import { useTrackedLocalStorage } from "./use-tracked-browser-storage";

// Constants
const MAX_RECENT_PERSONS = 10;

// Minimal type definition for a recent person with only essential fields for storage
export interface RecentPerson {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email_address: string | null;
  job_title: string | null;
  timestamp: number;
}

export function usePersonSelection() {
  const navigate = useNavigate();
  const params = useParams<{ personId: string }>();
  const location = useLocation();

  // Manage selected person and recent persons with automatic organization scoping
  const [selectedPersonId, setSelectedPersonId] =
    useTrackedLocalStorage<string>(
      "persons::explorer::selected-person::active",
      "",
    );
  const [recentPersons, setRecentPersons] = useTrackedLocalStorage<
    RecentPerson[]
  >("persons::explorer::selected-person::recent", []);

  // Get person ID from URL path param only
  const personIdFromPath = params.personId;
  const storedPersonId = selectedPersonId;

  const currentSelectedPersonId = personIdFromPath || storedPersonId;

  // Sort recent persons by timestamp (most recent first)
  const sortedRecentPersons = useMemo(() => {
    return [...recentPersons].sort((a, b) => b.timestamp - a.timestamp);
  }, [recentPersons]);

  // Find the selected person in recent persons list
  const initialPerson = useMemo(() => {
    if (!currentSelectedPersonId || recentPersons.length === 0)
      return undefined;
    return recentPersons.find(
      (person) => person.id === currentSelectedPersonId,
    );
  }, [currentSelectedPersonId, recentPersons]);

  // Handle person selection
  const handlePersonChange = useCallback(
    (value: string, person: Person | RecentPerson | null) => {
      if (value) {
        // Navigate to the person explorer and preserve query
        const target = {
          pathname: `/persons/${value}/explorer`,
          search: location.search,
        };
        const isSame =
          location.pathname === target.pathname &&
          location.search === (target.search || "");
        if (!isSame) {
          navigate(target, { replace: true });
        }

        // Also update localStorage for persistence across page loads
        setSelectedPersonId(value);

        // Add to recent persons if person object is provided
        if (person) {
          // Extract minimal fields and add with current timestamp (works for both full and recent objects)
          setRecentPersons((prev) => {
            const filtered = (prev || []).filter((p) => p.id !== person.id);
            return [
              {
                id: person.id,
                first_name: person.first_name,
                last_name: person.last_name,
                email_address: person.email_address,
                job_title: person.job_title,
                timestamp: Date.now(),
              },
              ...filtered,
            ].slice(0, MAX_RECENT_PERSONS);
          });
        }
      } else {
        // Remove from localStorage only
        setSelectedPersonId("");
      }
    },
    [
      navigate,
      setRecentPersons,
      setSelectedPersonId,
      location.pathname,
      location.search,
    ],
  );

  // Initialize URL from localStorage if needed
  useEffect(() => {
    // If we have a stored selection but no URL param, update the URL
    if (!personIdFromPath && storedPersonId && selectedPersonId) {
      // For explorer pages, use the URL pattern
      if (location.pathname === "/persons/explorer") {
        navigate(`/persons/${storedPersonId}/explorer`, { replace: true });
      }
    }
  }, [
    personIdFromPath,
    storedPersonId,
    selectedPersonId,
    navigate,
    location.pathname,
  ]);

  return {
    selectedPersonId: currentSelectedPersonId,
    initialPerson,
    handlePersonChange,
    recentPersons: sortedRecentPersons,
  };
}
